import { useState, useEffect } from "react";
import { statusAgendamento } from "../Agendamento/AgendamentoContext";
import { AtendimentoGrupo } from "../../api/api";
import { useAuth } from "../Auth/AuthContext";
import { useMessage } from "../Message/MessageContext";

export const useSave = (id) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(null);
  const { setWarning, setError } = useMessage();
  const { user } = useAuth();
  const [pessoaBuscaDialog, setPessoaBuscaDialog] = useState(false);
  const [pacientes, setPacientes] = useState([]);
  const [setorAtendimento, setSetorAtendimento] = useState(null);
  const [profissional, setProfissional] = useState(null);
  const [profissionalAdicional, setProfissionalAdicional] = useState([]);
  const [status, setStatus] = useState(statusAgendamento.emAberto);
  const [descricao, setDescricao] = useState("");
  const [dataAgendamento, setDataAgendamento] = useState(null);
  const [ausenciaJustificada, setAusenciaJustificada] = useState(false);
  const [savedItem, setSavedItem] = useState(null);
  const [dataAtendimento, setDataAtendimento] = useState(null);
  const [dtRecorrencias, setDtRecorrencias] = useState([]);

  const save = async (params = null) => {
    if (!dataAgendamento && dataAgendamento < new Date()) {
      setWarning("A data de agendamento deve ser no futuro");
      return;
    }

    if (pacientes.length < 2) {
      setWarning("Um atendimento em grupo deve ter pelo menos 2 pacientes");
      return;
    }

    const isAgendamento = params?.isAgendamento ?? true;
    const newStatus = params?.status ?? status;

    const pacientesWithUpdatedStatus = pacientes.map(({ status, ...rest }) => ({
      ...rest,
      status:
        status === statusAgendamento.emAberto
          ? statusAgendamento.realizado
          : status,
    }));

    const atendimentoData = {
      dataAgendamento,
      descricao,
      setorAtendimento: setorAtendimento.id,
      ausenciaJustificada,
      profissional: profissional.id,
      profissionalAdicional: profissionalAdicional.map((p) => ({
        profissional: p.id,
      })),
      pessoas: pacientesWithUpdatedStatus.map(
        ({ id, descricao, status, ausenciaJustificada }) => ({
          id,
          descricao,
          status,
          ausenciaJustificada,
        })
      ),
      dataAtendimento: isAgendamento ? dataAtendimento : new Date(),
      status: newStatus,
      dtRecorrencias,
    };

    setIsSaving(true);
    setPacientes(pacientesWithUpdatedStatus);

    const { data } = id
      ? await AtendimentoGrupo(user.jwt).update(id, atendimentoData)
      : await AtendimentoGrupo(user.jwt).add(atendimentoData);

    setDataAtendimento(new Date(data.mainAtendimento.dataAtendimento));

    setSavedItem(data);
    setIsSaving(false);
  };

  const clear = () => {
    setPacientes([]);
    setSetorAtendimento(null);
    setProfissional(null);
    setProfissionalAdicional([]);
    setDataAgendamento(null);
    setDescricao("");
    setSavedItem(null);
    setDataAtendimento(null);
    setDtRecorrencias([]);
  };

  const loadAtendimento = async () => {
    if (!id) {
      return clear();
    }

    setIsLoading(true);
    try {
      const {
        data: { mainAtendimento, individualAtendimentos },
      } = await AtendimentoGrupo(user.jwt).get(id);

      setDataAgendamento(new Date(mainAtendimento.dataAgendamento));
      setPacientes(
        individualAtendimentos.map(
          ({ pessoa, descricao, ausenciaJustificada, status }) => ({
            ...pessoa,
            descricao,
            ausenciaJustificada,
            status,
          })
        ) || []
      );
      setSetorAtendimento(mainAtendimento.setorAtendimento);
      setProfissional(mainAtendimento.profissional);
      setProfissionalAdicional(mainAtendimento.profissionalAdicional);
      setDescricao(mainAtendimento.descricao);
      setAusenciaJustificada(mainAtendimento.ausenciaJustificada);
      setDataAtendimento(
        mainAtendimento.dataAtendimento
          ? new Date(mainAtendimento.dataAtendimento)
          : null
      );

      //while editing, Recorrencia is not allowed to be changed
      setDtRecorrencias([]);
    } catch (error) {
      setError("Erro ao carregar dados do atendimento em grupo");
    } finally {
      setIsLoading(false);
    }
  };

  const removePaciente = (paciente) => {
    setPacientes(pacientes.filter((p) => p.id !== paciente.id));
  };

  const updatePaciente = (paciente) => {
    setPacientes(pacientes.map((p) => (p.id === paciente.id ? paciente : p)));
  };

  const addPaciente = (newPaciente) => {
    if (newPaciente.desligada) {
      setWarning(
        `O paciente ${newPaciente.nome} está desligado, e portanto não pode ser atendido`
      );
      return;
    }

    setPacientes([
      ...pacientes.filter((p) => p.id !== newPaciente.id),
      newPaciente,
    ]);
  };

  useEffect(() => {
    loadAtendimento();
  }, [id]);

  const canEdit = (() => {
    const profissionalUserId = profissional?.user?.id ?? profissional?.user;
    return (
      profissionalUserId === user.user.id ||
      profissionalAdicional?.some(
        (p) => (p.profissional?.user || p.user?.id) === user.user.id
      )
    );
  })();

  return {
    save,
    isLoading,
    pessoaBuscaDialog,
    setPessoaBuscaDialog,
    pacientes,
    removePaciente,
    setorAtendimento,
    setSetorAtendimento,
    profissional,
    setProfissional,
    profissionalAdicional,
    setProfissionalAdicional,
    isSaving,
    status,
    setStatus,
    descricao,
    setDescricao,
    dataAgendamento,
    setDataAgendamento,
    addPaciente,
    canEdit,
    savedItem,
    ausenciaJustificada,
    setAusenciaJustificada,
    updatePaciente,
    dataAtendimento,
    dtRecorrencias,
    setDtRecorrencias,
    clear,
  };
};
